(() => {
  'use strict';
  if (window.__lfcCartToolsReady) return;
  window.__lfcCartToolsReady = true;

  const shippingRequests = new WeakMap();
  const couponRequests = new WeakMap();
  const text = (root, key, fallback) => root?.dataset?.[key] || fallback;
  const moneyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

  function storefrontRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function cartUrl(suffix = '') {
    const configured = window.routes?.cart_url;
    if (configured) return `${configured.replace(/\/$/, '')}${suffix}`;
    return `${storefrontRoot()}cart${suffix}`;
  }

  function cartSignature(cart) {
    return (cart?.items || [])
      .map((item) => `${item.key || item.variant_id}:${item.quantity}`)
      .sort()
      .join('|');
  }

  function isCurrentRoot(root) {
    return Boolean(root?.isConnected && root.matches('cart-tools'));
  }

  async function currentCart(signal) {
    const response = await fetch(`${cartUrl('.js')}?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  }

  function provinceFromZip(zip) {
    const value = Number(zip);
    const ranges = [
      [1000000, 19999999, 'SP'],
      [20000000, 28999999, 'RJ'],
      [29000000, 29999999, 'ES'],
      [30000000, 39999999, 'MG'],
      [40000000, 48999999, 'BA'],
      [49000000, 49999999, 'SE'],
      [50000000, 56999999, 'PE'],
      [57000000, 57999999, 'AL'],
      [58000000, 58999999, 'PB'],
      [59000000, 59999999, 'RN'],
      [60000000, 63999999, 'CE'],
      [64000000, 64999999, 'PI'],
      [65000000, 65999999, 'MA'],
      [66000000, 68899999, 'PA'],
      [68900000, 68999999, 'AP'],
      [69000000, 69299999, 'AM'],
      [69300000, 69399999, 'RR'],
      [69400000, 69899999, 'AM'],
      [69900000, 69999999, 'AC'],
      [70000000, 72799999, 'DF'],
      [72800000, 72999999, 'GO'],
      [73000000, 73699999, 'DF'],
      [73700000, 76799999, 'GO'],
      [76800000, 76999999, 'RO'],
      [77000000, 77999999, 'TO'],
      [78000000, 78899999, 'MT'],
      [79000000, 79999999, 'MS'],
      [80000000, 87999999, 'PR'],
      [88000000, 89999999, 'SC'],
      [90000000, 99999999, 'RS'],
    ];
    return ranges.find(([start, end]) => value >= start && value <= end)?.[2] || '';
  }

  function shippingUrl(zip, province) {
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', zip);
    params.set('shipping_address[country]', 'Brazil');
    params.set('shipping_address[province]', province);
    params.set('t', String(Date.now()));
    return `${cartUrl('/shipping_rates.json')}?${params}`;
  }

  function discountUrl(code) {
    const redirect = encodeURIComponent(cartUrl('.js'));
    return `${storefrontRoot()}discount/${encodeURIComponent(code)}?redirect=${redirect}`;
  }

  function setBusy(root, selector, kind, busy, loadingLabel) {
    if (!isCurrentRoot(root)) return;
    const button = root.querySelector(selector);
    if (!button) return;
    if (!button.dataset.label) button.dataset.label = button.textContent.trim();
    button.disabled = busy;
    button.textContent = busy ? loadingLabel : button.dataset.label;
    root.toggleAttribute(`data-${kind}-busy`, busy);
    root.toggleAttribute(
      'aria-busy',
      root.hasAttribute('data-shipping-busy') || root.hasAttribute('data-coupon-busy')
    );
  }

  function setShippingBusy(root, busy) {
    setBusy(root, '[data-cep-apply]', 'shipping', busy, 'Calculando…');
  }

  function setCouponBusy(root, busy) {
    setBusy(root, '[data-coupon-apply]', 'coupon', busy, 'Aplicando…');
  }

  function restoreZip(root) {
    const input = root?.querySelector('[data-cep-input]');
    if (!input || input.value) return;
    try {
      input.value = localStorage.getItem('lfc_cart_shipping_zip') || '';
    } catch (_) {}
  }

  function cancelRequest(requests, root) {
    const active = requests.get(root);
    if (!active) return;
    clearTimeout(active.timeout);
    active.controller.abort();
    requests.delete(root);
  }

  function cancelShipping(root) {
    cancelRequest(shippingRequests, root);
  }

  function cancelCoupon(root) {
    cancelRequest(couponRequests, root);
  }

  function clearShippingQuote(root, messageText = '') {
    if (!root) return;
    cancelShipping(root);
    root.removeAttribute('data-cart-signature');
    root.querySelector('[data-cep-rates]')?.replaceChildren();
    const message = root.querySelector('[data-cep-msg]');
    if (message) {
      message.textContent = messageText;
      message.classList.remove('is-ok');
    }
    setShippingBusy(root, false);
  }

  function invalidateCartTools() {
    document.querySelectorAll('cart-tools').forEach((root) => {
      clearShippingQuote(root);
      cancelCoupon(root);
      setCouponBusy(root, false);
    });
  }

  function subscribeToCartUpdates() {
    if (window.__lfcCartToolsCartSubscription) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    window.__lfcCartToolsCartSubscription = subscribe(PUB_SUB_EVENTS.cartUpdate, invalidateCartTools);
  }

  function togglePanel(toggle) {
    const root = toggle.closest('cart-tools');
    const key = toggle.dataset.toolsToggle;
    const panel = root?.querySelector(`[data-tools-panel="${key}"]`);
    if (!root || !panel) return;
    const open = panel.hidden;

    root.querySelectorAll('[data-tools-panel]').forEach((candidate) => {
      const candidateKey = candidate.dataset.toolsPanel;
      const candidateToggle = root.querySelector(`[data-tools-toggle="${candidateKey}"]`);
      const candidateOpen = candidate === panel && open;
      candidate.hidden = !candidateOpen;
      candidateToggle?.classList.toggle('is-open', candidateOpen);
      candidateToggle?.setAttribute('aria-expanded', candidateOpen ? 'true' : 'false');
    });

    if (!open) return;
    if (key === 'shipping') restoreZip(root);
    const input = panel.querySelector('input');
    requestAnimationFrame(() => {
      if (!isCurrentRoot(root) || panel.hidden) return;
      try {
        input?.focus({ preventScroll: true });
      } catch (_) {
        input?.focus();
      }
      panel.scrollIntoView({ block: 'nearest' });
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const toggle = target.closest('[data-tools-toggle]');
    if (toggle) {
      event.preventDefault();
      togglePanel(toggle);
      return;
    }

    const couponButton = target.closest('[data-coupon-apply]');
    if (couponButton) {
      event.preventDefault();
      applyCoupon(couponButton.closest('cart-tools'));
      return;
    }

    const zipButton = target.closest('[data-cep-apply]');
    if (zipButton) {
      event.preventDefault();
      calculateShipping(zipButton.closest('cart-tools'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const target = event.target instanceof Element ? event.target : null;
    const root = target?.closest('cart-tools');
    if (target?.matches('[data-coupon-input]')) {
      event.preventDefault();
      applyCoupon(root);
    }
    if (target?.matches('[data-cep-input]')) {
      event.preventDefault();
      calculateShipping(root);
    }
  });

  document.addEventListener('input', (event) => {
    const target = event.target instanceof HTMLInputElement ? event.target : null;
    if (!target) return;
    const root = target.closest('cart-tools');

    if (target.matches('[data-coupon-input]')) {
      cancelCoupon(root);
      setCouponBusy(root, false);
      const message = root?.querySelector('[data-coupon-msg]');
      if (message) {
        message.textContent = '';
        message.classList.remove('is-ok');
      }
      return;
    }

    if (!target.matches('[data-cep-input]')) return;
    let value = target.value.replace(/\D/g, '').slice(0, 8);
    if (value.length > 5) value = `${value.slice(0, 5)}-${value.slice(5)}`;
    target.value = value;
    clearShippingQuote(root);
  });

  async function calculateShipping(root) {
    if (!isCurrentRoot(root) || shippingRequests.has(root)) return;
    const input = root.querySelector('[data-cep-input]');
    const message = root.querySelector('[data-cep-msg]');
    const list = root.querySelector('[data-cep-rates]');
    if (!input || !message || !list) return;

    const zip = input.value.replace(/\D/g, '');
    list.replaceChildren();
    message.classList.remove('is-ok');
    const province = provinceFromZip(zip);
    if (zip.length !== 8 || new Set(zip).size <= 1 || !province) {
      message.textContent = text(root, 'zipInvalid', 'Digite um CEP válido com 8 dígitos.');
      input.focus();
      return;
    }

    const controller = new AbortController();
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 20000);
    shippingRequests.set(root, { controller, timeout });
    setShippingBusy(root, true);
    message.textContent = text(root, 'zipLoading', 'Calculando…');
    try {
      localStorage.setItem('lfc_cart_shipping_zip', input.value);
    } catch (_) {}

    try {
      const cartBefore = await currentCart(controller.signal);
      if (!isCurrentRoot(root)) return;
      if (!cartBefore.items?.length) {
        message.textContent = 'Seu carrinho está vazio.';
        return;
      }

      const response = await fetch(shippingUrl(zip, province), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!isCurrentRoot(root)) return;
      if (!response.ok) throw new Error(data?.message || String(response.status));

      const cartAfter = await currentCart(controller.signal);
      if (!isCurrentRoot(root)) return;
      const beforeSignature = cartSignature(cartBefore);
      const afterSignature = cartSignature(cartAfter);
      if (beforeSignature !== afterSignature) {
        message.textContent = text(root, 'zipCartChanged', 'O carrinho mudou durante a cotação. Calcule novamente.');
        return;
      }

      const rates = Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
      if (!rates.length) {
        message.textContent = text(root, 'zipEmpty', 'O frete será confirmado no checkout.');
        return;
      }

      rates
        .sort((first, second) => Number(first.price) - Number(second.price))
        .forEach((rate) => {
          const item = document.createElement('li');
          item.className = 'cart-tools__rate';
          const name = document.createElement('span');
          name.textContent = rate.name || 'Frete';
          const price = document.createElement('strong');
          price.textContent = Number.parseFloat(rate.price) === 0
            ? 'Grátis'
            : moneyFormatter.format(Number(rate.price) || 0);
          item.append(name, price);
          list.append(item);
        });

      root.dataset.cartSignature = afterSignature;
      const quantity = Number(cartAfter.item_count) || cartAfter.items.length;
      message.textContent = `${rates.length} ${rates.length === 1 ? 'opção' : 'opções'} para ${quantity} ${quantity === 1 ? 'item' : 'itens'} do carrinho.`;
      message.classList.add('is-ok');
      message.scrollIntoView({ block: 'nearest' });
    } catch (error) {
      if (!isCurrentRoot(root)) return;
      if (error.name === 'AbortError' && !timedOut) return;
      message.textContent = error.name === 'AbortError'
        ? text(root, 'zipTimeout', 'A cotação demorou mais que o esperado. Tente novamente.')
        : text(root, 'zipError', 'O frete será confirmado no checkout.');
    } finally {
      const active = shippingRequests.get(root);
      if (active?.controller === controller) {
        clearTimeout(timeout);
        shippingRequests.delete(root);
        setShippingBusy(root, false);
      }
    }
  }

  async function applyCoupon(root) {
    if (!isCurrentRoot(root) || couponRequests.has(root)) return;
    const input = root.querySelector('[data-coupon-input]');
    const message = root.querySelector('[data-coupon-msg]');
    if (!input || !message) return;

    const code = input.value.trim();
    message.classList.remove('is-ok');
    if (!code) {
      message.textContent = text(root, 'couponEmpty', 'Digite um código.');
      input.focus();
      return;
    }

    const controller = new AbortController();
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 15000);
    couponRequests.set(root, { controller, timeout });
    setCouponBusy(root, true);
    message.textContent = text(root, 'couponLoading', 'Aplicando…');

    try {
      const response = await fetch(discountUrl(code), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        redirect: 'follow',
        signal: controller.signal,
      });
      if (!isCurrentRoot(root)) return;
      if (!response.ok) throw new Error(String(response.status));
      const cart = await response.json().catch(() => ({}));
      if (!isCurrentRoot(root)) return;
      const appliedCode = (cart.discount_codes || []).find(
        (discount) => discount.code?.toLocaleLowerCase('pt-BR') === code.toLocaleLowerCase('pt-BR')
      );
      if (appliedCode?.applicable === false) {
        message.textContent = 'Cupom inválido ou não aplicável a este carrinho.';
        message.classList.remove('is-ok');
        input.focus();
        return;
      }
      message.textContent = text(root, 'couponSuccess', 'Cupom registrado para o checkout.');
      message.classList.add('is-ok');
      message.scrollIntoView({ block: 'nearest' });
    } catch (error) {
      if (!isCurrentRoot(root)) return;
      if (error.name === 'AbortError' && !timedOut) return;
      message.textContent = timedOut
        ? 'A aplicação demorou mais que o esperado. Tente novamente.'
        : text(root, 'couponError', 'Não foi possível aplicar o cupom agora.');
      message.classList.remove('is-ok');
    } finally {
      const active = couponRequests.get(root);
      if (active?.controller === controller) {
        clearTimeout(timeout);
        couponRequests.delete(root);
        setCouponBusy(root, false);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', subscribeToCartUpdates, { once: true });
  } else {
    subscribeToCartUpdates();
  }
})();
